import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ApexAnnotations } from 'ng-apexcharts';
import { ChartOptions, PlanV2 } from 'src/app/core/models';
import { WeeklyReportV2 } from 'src/app/core/models/v2/weekly-report.v2.model';


@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss']
})
export class ChartComponent implements OnChanges {

  @ViewChild("chart") chart:
    | {
      updateOptions: (options: Partial<ChartOptions>) => void;
      dataURI: () => Promise<{ imgURI?: string }>;
    }
    | undefined;
  @Input() graphic!:WeeklyReportV2[];
  @Input() plan!:PlanV2;
  @Output() imagesGenerated = new EventEmitter<string[]>(); // Emitir imágenes al padre

  chartOptionsOne: Partial<ChartOptions> = {};
  data:{x:number, y:number}[] = []; //datos del grafico
  serie1:{x:string, y:number | null}[] = []; //serie baseline
  serie2:{x:string, y:number | null}[] = []; //serie plan
  annotations: ApexAnnotations = {}; //conjunto de lineas verticales en el grafico (STO, LTO)
  maxDate:number = 0; //valor maximo del eje x
  yMax!:number; //valor maximo del eje y

  // Arreglo para almacenar las imágenes generadas
  chartImages: string[] = [];

  // objeto con los colores definidos para los STO y LTO
  objectivesColors:{objective:string, color:string}[] = [
    {
      objective: 'STO1',
      color: '#0000ff' //blue
    },
    {
      objective: 'STO2',
      color: '#ffa500' //orange
    },
    {
      objective: 'STO3',
      color: '#008000' //green
    },
    {
      objective: 'STO4',
      color: '#ffff00' //yellow
    },
    {
      objective: 'STO5',
      color: '#ee82ee' //violet
    },
    {
      objective: 'STO6',
      color: '#ffc0cb' //pink
    },
    {
      objective: 'STO7',
      color: '#ff00ff' //fuchsia
    },
    {
      objective: 'STO8',
      color: '#800000' //maroon
    },
    {
      objective: 'STO9',
      color: '#9acd32' //yellow-green
    },
    {
      objective: 'STO10',
      color: '#008080' //teal
    },
    {
      objective: 'STO11',
      color: '#ff6347' // tomato
    },
    {
      objective: 'STO12',
      color: '#4b0082' //indigo
    },
    {
      objective: 'STO13',
      color: '#00008b' //dark blue
    },
    {
      objective: 'STO14',
      color: '#7fff00' //chartreuse
    },
    {
      objective: 'LTO',
      color: '#000000' //black
    }
  ];

  // objeto para los colores de los STO (lineas verticales en el gráfico) que si tienen un end_date
  objectivesActives:{objective:string, color:string}[]=[];

  constructor(){}

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['graphic'] || changes['plan']){

      // Check if plan exists before proceeding
      if (!this.plan) {
        return;
      }

      // obtener datos a graficar en caso de haber datos
      if(this.graphic && this.graphic.length>0){
        this.data = [];
        this.graphic.forEach(element => {
          this.data.push(
            {
              x: new Date(element.week_end).getTime(),
              y: element.value
            }
          );
        });

        // ordenar datos por fecha
        this.data.sort((a,b) => a.x - b.x);

        // defino fecha maxima como fecha mayor del grafico en caso de que haya datos para graficar
        if(this.data.length){
          this.maxDate = this.data[this.data.length-1].x;
        }

      }

      // defino yMax del grafico
      if(['replacement','caregiver_training','teacher_training'].includes(this.plan.category)){
        this.yMax = 100; // % para replacement, caregiver y teacher
      }
      else if(this.plan.category ==='maladaptive'){
        if(this.data.length){
          /* si this.data tiene datos => elijo el max (redondeado) entre ymax de this.data y baseline_level y sumo 20
          _si el valor ymax de data es 33 y baseline_level<33 => this.yMax = 60 (40 + 20)
          _si el valor ymax de data es 33 y baseline_level=45 (>33) => this.yMax = 70 (50 + 20)
          */
          const baselineLevel = Number(this.plan.baseline_level ?? 0);
          this.yMax = Math.ceil( Math.max((Math.max(...this.data.map(obj => obj.y))), baselineLevel) / 10 ) * 10 + 20;
        }
        else{
          // si this.data no tiene datos => tomo el baseline_level redondeado y sumo 20
            this.yMax = Math.ceil(Number(this.plan.baseline_level ?? 0) / 10) * 10 + 20;
        }

      }

      // set gráfico
      this.setGraphic();
    }
    // end
  }

  private setGraphic(){
    // Check if plan exists before proceeding
    if (!this.plan) {
      console.warn('Plan data not available for chart generation');
      return;
    }

    // verifico si hay datos en this.data
    if(this.data.length>0){
      // si hay datos en this.data
      this.serie1.push(
        {
          x: this.plan.baseline_date ? this.formatDate2(new Date(this.plan.baseline_date)) : '',
            y: Number(this.plan.baseline_level ?? 0)
        },
        {
          x:'',
          y:null
        }
      );
      this.serie2.push(
        {
          x: this.plan.baseline_date ? this.formatDate2(new Date(this.plan.baseline_date)) : '',
          y: null
        },
        {
          x:'',
          y:null
        }
      );
      const categorias_format = [];
      categorias_format.push(
        this.plan.baseline_date ? this.formatDate2(new Date(this.plan.baseline_date)) : '',
        ''
      );

      for(let i=0; i<this.data.length; i++){
        this.serie1.push(
          {
            x: this.formatDate(this.data[i].x),
            y: null
          }
        );
        this.serie2.push(
          {
            x:this.formatDate(this.data[i].x),
            y: this.data[i].y
          }
        );
        categorias_format.push(this.formatDate(this.data[i].x))
      }

      // establecer annotations (lineas verticales en el grafico para los STO y LTO)
      this.setAnnotations();

      // configurar grafico con la variable chartOptionsOne
      this.chartOptionsOne = {
        chart: {
          animations: {
            enabled: false
          },
          height: 370,
          type: 'line',
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        grid: {
          show: true,
          xaxis: {
            lines: {
              show: false
            }
          },
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        dataLabels: {
          enabled: true
        },
        stroke: {
          // curve: 'smooth'
        },
        tooltip: {
          enabled: false
        },
        series: [
          {
            name: "Baseline",
            type: "scatter", // Punto aislado, baseline
            color: "#FF0000",
            data: this.serie1
          },
          {
            name: "Number of Occurrences",
            color: '#00D3C7',
            type: "line",
            data: this.serie2
          },
        ],
        xaxis: {
          type: "categories",
          categories: categorias_format,
          labels: {
            show: true,
            datetimeUTC: false,
            rotate: -45,
            rotateAlways: true,
            style: {
              fontSize: '10px'
            },
          },
        },
        yaxis: {
          min: 0,
          max: this.yMax
        },
        legend: {
          show: false
        },
        annotations: this.annotations
      }

      this.reloadGraphic();
    }
    else{
      // si no hay datos para graficar => graficar solo el baseline

      // configurar grafico con la variable chartOptionsOne
      this.chartOptionsOne = {
        chart: {
          height: 370,
          type: 'line',
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        grid: {
          show: true,
          xaxis: {
            lines: {
              show: false
            }
          },
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        dataLabels: {
          enabled: true
        },
        stroke: {
          // curve: 'smooth'
        },
        tooltip: {
          enabled: false
        },
        series: [
          {
            name: "Baseline",
            type: "scatter", // Punto aislado, baseline
            color: "#FF0000",
            data: [
              {
                x: this.plan.baseline_date ? this.formatDate2(new Date(this.plan.baseline_date)) : '',
                y: this.plan.baseline_level
              }
            ]
          }
        ],
        xaxis: {
          type: "categories",
          categories: [this.plan.baseline_date ? this.formatDate2(new Date(this.plan.baseline_date)) : '', ],
          labels: {
            show: true,
            datetimeUTC: false,
            rotate: -45,
            rotateAlways: true,
            style: {
              fontSize: '10px'
            },
          },
        },
        yaxis: {
          min: 0,
          max: this.yMax
        },
        legend: {
          show: false
        }
      }
      this.reloadGraphic();
    }
    // end
    this.generateImagesChart();
  }

  getYAxisLabel(): string {
    if (!this.plan || this.plan.category !== 'maladaptive') {
      return '% of Trials';
    }

    const unitType = this.plan.unit_type || 'occurrences';
    const isPerHour = this.plan.timeframe === 'per_hour';

    if (unitType === 'occurrences') {
      return isPerHour ? 'Incidents per Hour' : 'Frequency';
    }

    const unitLabel = unitType === 'seconds' ? 'Seconds' : 'Minutes';
    return isPerHour ? `Duration (${unitLabel}) per Hour` : `Duration (${unitLabel})`;
  }

  private formatDate(value:number){
    const isoDate = new Date(value).toISOString().split('T')[0];
    const [year, month, day] = isoDate.split('-');

    return `${month}/${day}/${year}`;
  }

  private formatDate2(fecha: Date): string {
    const mm = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const dd = fecha.getDate().toString().padStart(2, '0');
    const yyyy = fecha.getFullYear();

    return `${mm}/${dd}/${yyyy}`;
  }

  private setAnnotations(){
    // Check if plan exists before proceeding
    if (!this.plan || !this.plan.objectives) {
      console.warn('Plan or objectives data not available for annotations');
      return;
    }

    this.annotations = {};
    this.objectivesActives = [];

    // poner linea que separa baseline de la serie de datos
    this.annotations = {
      xaxis: [
        {
          x: '', // ✅ Línea vertical entre baseline y punto inicial de la serie
          borderColor: "#FF0000",
          strokeDashArray: 0,
          borderWidth: 2,
          opacity: 0.9,
          label: {
            text: "Treatment",
            orientation: "horizontal",
            position: "top",
            offsetX: 100, // Positive value moves the label to the right of the line
            style: {
              color: "#FFFFFF",
              background: "#FF0000",
              fontSize: '16px',
              fontWeight: 'bold',
            }
          }
        }
      ],
    }

    const xaxis: NonNullable<ApexAnnotations['xaxis']> = []; //array auxiliar para agregar los STO / LTO

    // recorrer los objetivos
    for(let i=0; i<this.plan?.objectives.length; i++){
      const objective = this.plan.objectives[i];
      const objectiveEndDate = objective?.end_date;
      if (!objectiveEndDate) {
        continue;
      }
      const objectiveDate = new Date(objectiveEndDate).getTime();

      // verifico si es STO o LTO
      if(objective.type ==='STO'){
        const objectiveColor = this.objectivesColors.find(item => item.objective===`STO${i+1}`) ?? { objective: `STO${i+1}`, color: '#000000' };

        // agrego STO
        xaxis.push({
          x: this.formatDate(objectiveDate), // ✅ Línea vertical en STO
          borderColor: objectiveColor.color,
          strokeDashArray: 0,
          opacity: 0.9,
          borderWidth: 2,
          label: {
            text: `STO${i+1}`,
            position: "top",
            style: {
              color: objectiveColor.color,
              background: "#FFFFFF",
              fontSize: "12px",
              fontWeight: "bold",
            }
          }
        });

        if(this.compareDates(this.formatDate(this.maxDate), this.formatDate(objectiveDate))){
          this.objectivesActives.push(objectiveColor);
        }

      }
      else if(objective.type ==='LTO'){
        const objectiveColor = this.objectivesColors.find(item => item.objective===`LTO`) ?? { objective: 'LTO', color: '#000000' };

        xaxis.push({
          x: this.formatDate(objectiveDate), // ✅ Línea vertical en LTO
          borderColor: objectiveColor.color,
          strokeDashArray: 0,
          opacity: 0.9,
          borderWidth: 2,
          label: {
            text: "LTO",
            position: "top",
            style: {
              color: objectiveColor.color,
              background: "#FFFFFF",
              fontSize: "12px",
              fontWeight: "bold"
            }
          }
        });

        if(this.compareDates(this.formatDate(this.maxDate), this.formatDate(objectiveDate))){
          this.objectivesActives.push(objectiveColor);
        }
      }
    }

    // agregar xaxis a annotations
    this.annotations = {
      xaxis: [
          ...(this.annotations.xaxis ?? []),
      ].concat(xaxis)
    }

    // end
  }

  public reloadGraphic(){
    // recargar gráfico con las annotations e yMax
    const chartRef = this.chart;
    if (chartRef) {
      requestAnimationFrame(() => {
        chartRef.updateOptions(this.chartOptionsOne);
      });
    }
  }

  private async generateImagesChart() {
    // Wait longer for the chart to be fully rendered (increased from 500ms to 1000ms)
    setTimeout(() => {
      this.captureChartImage();
    }, 1000);
  }

  /**
   * Captures the chart image and emits it to the parent component
   * @returns Promise<string> The chart image as base64 string
   */
  public async captureChartImage(): Promise<string> {
    if (!this.chart) {
      // console.warn('Chart component not available for capture');
      return '';
    }

    try {
      // Ensure chart is fully rendered before capturing
      await new Promise(resolve => setTimeout(resolve, 500));

      // Additional check to ensure chart is still available
      if (!this.chart) {
        console.warn('Chart component became unavailable during capture');
        return '';
      }

      // Get the chart image as base64 string
      const dataURI = await this.chart.dataURI();

      if (!dataURI) {
        console.warn('Failed to get chart dataURI');
        return '';
      }

      const imgURI = dataURI.imgURI;

      if (!imgURI) {
        // console.warn('Failed to capture chart image: Empty image URI');
        return '';
      }

      // Emit the image to the parent component
      this.imagesGenerated.emit([imgURI]);
      // console.log('Chart image captured successfully');

      return imgURI;
    } catch (error) {
      // console.error('Error capturing chart image:', error);
      return '';
    }
  }

  private compareDates(fecha1:string, fecha2:string){
    if(fecha1>=fecha2){
      return true;
    }
    else{
      return false;
    }
  }
}


